import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { useLang } from "@/i18n";
import { SlotGrid } from "@/pages/admin/AdminTeams";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Teams({ detectionId }) {
  const [teams, setTeams] = useState([]);
  const [visibles, setVisibles] = useState(true);
  const { t } = useLang();

  useEffect(() => {
    const url = detectionId ? `${API}/teams?detection_id=${detectionId}` : `${API}/teams`;
    axios.get(url).then((r) => setTeams(r.data)).catch(() => {});
    axios.get(`${API}/settings`).then((r) => setVisibles(r.data.equipes_visibles !== false)).catch(() => {});
  }, [detectionId]);

  return (
    <section id="equipes" data-testid="teams-section" className="py-28 lg:py-40 bg-[#0A0A0C] relative">
      <div className={`max-w-7xl mx-auto px-6 lg:px-10 transition-all duration-700 ${visibles ? "" : "blur-md select-none pointer-events-none"}`}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-8 mb-20"
        >
          <div>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("teams.eyebrow")}</p>
            <h2 className="font-display text-4xl md:text-5xl uppercase font-bold tracking-tight leading-tight">
              {t("teams.title.a")} <span className="text-[#C5A059]">{t("teams.title.b")}</span>
            </h2>
          </div>
          <p className="text-[#71717A] text-sm max-w-sm leading-relaxed">
            {t("teams.text")}
          </p>
        </motion.div>

        {teams.length === 0 && (
          <div className="border border-[#27272A] p-14 text-center">
            <p className="text-[#71717A] text-sm">{t("teams.empty")}</p>
            <a href="#detection" data-testid="teams-cta-detection" className="inline-block mt-6 border border-[#C5A059] text-[#C5A059] px-8 py-3.5 text-xs uppercase tracking-[0.25em] hover:bg-[#C5A059] hover:text-black transition-colors duration-300">
              {t("teams.cta")}
            </a>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {teams.map((team, i) => (
            <motion.div
              key={team.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="border border-[#27272A] bg-[#121214] p-8 lg:p-10"
              data-testid={`public-team-card-${team.numero}`}
            >
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-display text-2xl uppercase font-medium">{team.nom}</h3>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border ${team.statut === "complete" ? "border-[#C5A059] text-[#C5A059]" : "border-[#27272A] text-[#71717A]"}`}>
                  {team.statut === "complete" ? t("teams.complete") : `${team.joueurs.length}/11`}
                </span>
              </div>
              <div className="h-px bg-[#27272A] mt-5 relative">
                <div className="absolute inset-y-0 left-0 bg-[#C5A059] transition-[width] duration-700" style={{ width: `${(team.joueurs.length / 11) * 100}%` }} />
              </div>
              <SlotGrid team={team} />
              {team.match_prevu && (
                <p className="mt-6 text-sm">{t("teams.match")} <span className="text-[#C5A059]">{team.match_prevu}</span></p>
              )}
              <Link to={`/equipes/${team.id}`} data-testid={`team-page-link-${team.numero}`} className="inline-flex items-center gap-2 mt-6 text-[#C5A059] text-xs uppercase tracking-[0.25em] border-b border-[#C5A059]/40 pb-1 hover:border-[#C5A059] transition-colors">
                {t("teams.view")} <ArrowUpRight size={14} />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
      {!visibles && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <span className="bg-[#0A0A0C]/90 border border-[#C5A059] text-[#C5A059] text-xs uppercase tracking-[0.4em] px-6 py-3" data-testid="teams-coming-soon">
            Coming Soon
          </span>
        </div>
      )}
    </section>
  );
}
